import { Alert } from '@mui/material';
import AppDatePicker from 'components/FormElements/AppDatePicker';
import AppSelectBox from 'components/FormElements/AppSelectBox';
import FieldRow from 'components/FormElements/FieldRow';

import AdviserSelect from 'components/AdviserSelect';
import { InvestorFormValues } from 'helpers/investor/investorForm';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import InvestorAddress from './InvestorAddress';
import InvestorNationality from './InvestorNationality';
import SwitchField from './SwitchField';
import TextField from './TextField';
import CustomTagSection from 'components/CustomTagSelector';

import { honorificsDropdownList } from 'further-ui/labels';
import { CategoryOptions } from 'components/CustomTagSelector/types';
import { useDuplicateIndividualInvestorWarning } from 'hooks/data/investor/useDuplicateIndividualInvestorWarning';
import { isValid } from 'date-fns';
import { DuplicateInvestorAlert } from './Common/DuplicateInvestorAlert';
import { CalendarDay } from 'further-ui/utils';

type Props = {
  id?: string;
  emailInUseByOtherAccount: boolean;
  tagCategoryOptions: CategoryOptions;
  tagCategory?: string;
  tagOptions?: Array<string>;
  onCategoryChange: (category: string) => void;
};

const PersonalDetails: React.FC<Props> = ({
  id,
  emailInUseByOtherAccount,
  tagCategoryOptions,
  tagCategory,
  tagOptions,
  onCategoryChange,
}) => {
  const { control } = useFormContext<InvestorFormValues>();
  const isDeceased = useWatch({ control, name: 'isDeceased' });
  const isVulnerableClient = useWatch({ control, name: 'isVulnerableClient' });
  const [firstName, lastName, dob, firmId] = useWatch({
    control,
    name: ['firstName', 'lastName', 'dob', 'firmId'],
  });

  const { hasWarning, message } = useDuplicateIndividualInvestorWarning({
    firstName,
    lastName,
    dob: dob && isValid(dob) ? new CalendarDay(dob).toString() : undefined,
    firmId,
    ignoreInvestorId: id,
  });

  return (
    <>
      <FieldRow title="Title" centerTitle>
        <Controller
          name="title"
          control={control}
          render={({
            field: { ref, value, ...rest },
            fieldState: { error },
          }) => (
            <AppSelectBox
              {...rest}
              value={value ?? ''}
              data={honorificsDropdownList}
              valueKey="value"
              labelKey="label"
              variant="outlined"
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </FieldRow>
      <FieldRow title="First name*" centerTitle>
        <TextField name="firstName" control={control} />
      </FieldRow>
      <FieldRow title="Middle name" centerTitle>
        <TextField name="middleName" control={control} />
      </FieldRow>
      <FieldRow title="Last name*" centerTitle>
        <TextField name="lastName" control={control} />
      </FieldRow>
      <FieldRow title={id ? 'Email address' : 'User email*'}>
        <TextField name="email" control={control} />
        {emailInUseByOtherAccount ? (
          <Alert severity="info">
            This email is used with other accounts. If you change it and the new
            email is not in use in the system, the user will be asked to set a
            new password for the email. Their current email will remain in use
            with other accounts.
          </Alert>
        ) : null}
      </FieldRow>
      <FieldRow title="Phone number*" centerTitle>
        <TextField name="phoneNumber" control={control} />
      </FieldRow>
      <FieldRow title="Date of birth*" centerTitle>
        <Controller
          name="dob"
          control={control}
          render={({
            field: { ref, onChange, value, ...rest },
            fieldState: { error },
          }) => (
            <AppDatePicker
              {...rest}
              maxDate={new Date()} // should be set to today, and NOT to 18 years prior to current date
              inputRef={ref}
              error={!!error}
              value={value ?? null}
              helperText={error?.message}
              openOnFocus
              onChange={(date) => {
                onChange(date);
              }}
            />
          )}
        />
        {hasWarning && <DuplicateInvestorAlert message={message} />}
      </FieldRow>
      {id ? (
        <FieldRow title="Date registered*" centerTitle>
          <Controller
            name="dateRegister"
            control={control}
            render={({ field: { ref, ...rest }, fieldState: { error } }) => (
              <AppDatePicker
                {...rest}
                disabled
                onChange={() => {}}
                inputRef={ref}
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </FieldRow>
      ) : null}
      <FieldRow title="Job title" centerTitle>
        <TextField name="profession" control={control} />
      </FieldRow>
      <FieldRow title="Sector" centerTitle>
        <TextField name="sector" control={control} />
      </FieldRow>

      <FieldRow title="Will the investor be claiming tax relief?" centerTitle>
        <span>No</span>
        <SwitchField name="isClaimingTax" control={control} />
        <span>Yes</span>
      </FieldRow>
      {/* Address */}
      <InvestorAddress control={control} />
      <InvestorNationality control={control} />
      <FieldRow title="External investor ID" centerTitle>
        <TextField name="externalInvestorId" control={control} />
      </FieldRow>
      <FieldRow title="Vulnerable client" centerTitle>
        <span>No</span>
        <SwitchField name="isVulnerableClient" control={control} />
        <span>Yes</span>
      </FieldRow>
      {isVulnerableClient && (
        <FieldRow title="Vulnerability notes" labelSpacing={5}>
          <TextField
            name="vulnerabilityNotes"
            control={control}
            multiline
            rows={3}
          />
        </FieldRow>
      )}
      <FieldRow title="Deceased" centerTitle>
        <span>No</span>
        <SwitchField name="isDeceased" control={control} />
        <span>Yes</span>
      </FieldRow>
      {isDeceased && (
        <FieldRow title="Date of death*" centerTitle>
          <Controller
            name="dateOfDeath"
            control={control}
            render={({
              field: { ref, onChange, value, ...rest },
              fieldState: { error },
            }) => (
              <AppDatePicker
                {...rest}
                maxDate={new Date()}
                inputRef={ref}
                error={!!error}
                value={value ?? null}
                helperText={error?.message}
                openOnFocus
                onChange={(date) => {
                  onChange(date);
                }}
              />
            )}
          />
        </FieldRow>
      )}
      <FieldRow title="Requires hard copy reports" centerTitle>
        <span>No</span>
        <SwitchField name="requiresHardCopyReports" control={control} />
        <span>Yes</span>
      </FieldRow>
      <FieldRow title="UK remittance taxpayer (BIR)" centerTitle>
        <span>No</span>
        <SwitchField name="isUkRemittanceTaxpayer" control={control} />
        <span>Yes</span>
      </FieldRow>
      <FieldRow title="Custom tag(s)">
        <Controller
          name="customTags"
          control={control}
          render={({ field: { value, onChange } }) => (
            <CustomTagSection
              selectedCategory={tagCategory}
              categoryOptions={tagCategoryOptions}
              onCategorySelect={onCategoryChange}
              tagOptions={tagOptions}
              onChange={(newTags) => {
                onChange(newTags);
              }}
              tags={value ?? []}
            />
          )}
        />
      </FieldRow>
      {!id && (
        <FieldRow title="Default adviser" centerTitle>
          <Controller
            name="defaultAdviserId"
            control={control}
            render={({ field }) => (
              <AdviserSelect
                adviserId={field.value}
                onChange={(adviser) => field.onChange(adviser?._id ?? null)}
                placeholder="Select default adviser"
                fullWidth
                id="defaultAdviserId"
              />
            )}
          />
        </FieldRow>
      )}
    </>
  );
};

export default PersonalDetails;
